import Roles, {hasRoleAtLeast, isStaffOnlyRoleSet} from "../../shared/models/roles";
import {useGetUserRolesQuery} from "../generated/graphql";

/**
 * Rollen des eingeloggten Users als VOLLSTAENDIGER Satz.
 *
 * Bewusst nicht ueber `useHighestRole()`: dieser Hook faltet den Rollensatz auf eine
 * einzige Rolle zusammen und faellt fuer Logins, die nur `Assistant` oder `BetaTester`
 * haben, sogar auf `EndUser` zurueck. Fuer Autorisierungs-Gates ist das die falsche
 * Grundlage — der Server entscheidet immer ueber den ganzen Satz (#809).
 *
 * `loading` ist true, solange noch keine Rollen vom Server da sind. Gates sollen in
 * dieser Phase KEINE Aktion freischalten (fail-closed).
 */
export const useCurrentUserRoles = (): {roles: Array<Roles>; loading: boolean} => {
    const {data, loading} = useGetUserRolesQuery();
    const rolesOfCurrent = data?.getUserRoles?.rolesOfCurrent;
    const decided = !loading && !!rolesOfCurrent;

    return {roles: decided ? (rolesOfCurrent as Array<Roles>) : [], loading: !decided};
};

/**
 * Ist der aktuelle Login ein reiner Mitarbeitenden-Zugang (Kursleitung, Assistenz,
 * Staff, Bereichsleitung, BetaTester — ohne Admin-Rolle)?
 *
 * Spiegelt exakt den Server-Guard `isOnlyStaffAccess` (src/server/auth/staffAccessGuard.ts),
 * weil beide `isStaffOnlyRoleSet` aus `shared/models/roles` benutzen. Damit kann das UI
 * keine Aktionen mehr anbieten, die der Server mit `ForbiddenError` ablehnt (#809, #799).
 *
 * Waehrend des Ladens `true`: die restriktivere Annahme blendet Admin-Aktionen eher zu
 * viel als zu wenig aus.
 */
export const useIsStaffOnlyAccess = (): boolean => {
    const {roles, loading} = useCurrentUserRoles();

    return loading ? true : isStaffOnlyRoleSet(roles);
};

/**
 * Darf der aktuelle Login Resolver aufrufen, die mindestens `required` in der
 * Rollen-Hierarchie verlangen? Nutzt den vollen Rollensatz, damit kombinierte Rollen
 * (z. B. `Assistant` + `Admin`) korrekt als Admin gelten.
 */
export const useHasRoleAtLeast = (required: Roles): boolean => {
    const {roles, loading} = useCurrentUserRoles();

    return loading ? false : hasRoleAtLeast(roles, required);
};
